import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import { Badge, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ChatbotChannel } from '../../model/types';

export type ChannelScope = 'GENERAL' | ChatbotChannel;

export interface ChannelScopeTabsProps {
  value: ChannelScope;
  onChange: (value: ChannelScope) => void;
  /** Whether each channel currently has ≥1 overridden field on this card — drives the dot badge. */
  overrides: Record<ChatbotChannel, boolean>;
  /** The card's title, used only to build the tablist's accessible name. */
  cardTitle: string;
}

/**
 * Per-card General | Telegram | MAX scope switch (design §7.2/§7.4). Only the Behaviour and
 * Escalation & AI cards render this — the availability strip, the texts link card and the AI
 * provider card never do (the AI provider is a tenant-level resource, no channel concept).
 */
export function ChannelScopeTabs({ value, onChange, overrides, cardTitle }: ChannelScopeTabsProps) {
  const { t } = useTranslation('chatbot');

  const tabLabel = (channel: ChatbotChannel, label: string) =>
    overrides[channel] ? (
      <Badge
        variant="dot"
        color="primary"
        aria-label={t('scope.hasOverrides', { channel: t(`scope.${channel.toLowerCase()}`) })}
        sx={{ '& .MuiBadge-dot': { right: -8 } }}
      >
        {label}
      </Badge>
    ) : (
      label
    );

  return (
    <Tabs
      value={value}
      onChange={(_event, next: ChannelScope) => onChange(next)}
      textColor="primary"
      indicatorColor="primary"
      variant="scrollable"
      allowScrollButtonsMobile
      aria-label={t('scope.tabsAria', { card: cardTitle })}
      sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
    >
      <Tab value="GENERAL" label={t('scope.general')} />
      <Tab
        value="TELEGRAM"
        icon={<TelegramIcon fontSize="small" />}
        iconPosition="start"
        label={tabLabel('TELEGRAM', t('scope.telegram'))}
      />
      <Tab
        value="MAX"
        icon={<ChatRoundedIcon fontSize="small" />}
        iconPosition="start"
        label={tabLabel('MAX', t('scope.max'))}
      />
    </Tabs>
  );
}
